import { type FunctionComponent, useEffect, useState } from 'react';

import { Button } from '@dr.pogodin/react-utils';
import { captureException } from '@sentry/react';

import Throbber from 'components/Throbber';
import { AdminPage } from 'components/admin';
import AdminVideoUpload from 'components/admin/VideoUpload';
import { useArtist } from 'hooks';
import useUserArtist from 'hooks/useUserArtist';

import { deleteIntroVideo, uploadIntroVideo } from 'services/artists';

import {
  P_ADMIN_PRACTITIONERS,
  P_ADMIN_PROFILE,
  VIDEO_STATUS_REFRESH_INTERVAL,
  getId,
} from 'utils';

import IconVideo from 'assets/images/icon-video-24px.svg';

import noBorderButtonTheme from 'themes/Button/admin/no-border.scss';

import './style.scss';

type PropsT = {
  practitionerId?: string;
};

const AdminPractitionerIntroVideo: FunctionComponent<PropsT> = ({
  practitionerId,
}) => {
  const practitionerById = useArtist(practitionerId ?? '', {
    disabled: !practitionerId,
    withAllUserDetails: true,
  });
  const userArtistInternal = useUserArtist();
  const practitioner = practitionerId ? practitionerById : userArtistInternal;

  // We only want to run background reloads for the specific practitioner
  // we are currently viewing/editing.
  const hasIntroVideo = !!practitioner.data?.videoUrl;
  const isIntroVideoReady = !!practitioner.data?.videoIsReadyToBePlayed;
  const reloadPractitioner = practitioner.reload;

  useEffect(() => {
    let timerId: NodeJS.Timeout | undefined;

    if (hasIntroVideo && !isIntroVideoReady) {
      const iteration = () => {
        void (async () => {
          await reloadPractitioner();
          if (timerId !== undefined) {
            timerId = setTimeout(iteration, VIDEO_STATUS_REFRESH_INTERVAL);
          }
        })();
      };

      timerId = setTimeout(iteration, VIDEO_STATUS_REFRESH_INTERVAL);
    }

    return () => {
      clearTimeout(timerId);
      timerId = undefined;
    };
  }, [hasIntroVideo, isIntroVideoReady, reloadPractitioner]);

  const [busy, setBusy] = useState<boolean | number>(false);
  const isSaving = busy !== false;
  const [localVideo, setLocalVideo] = useState<File | string>();

  const THROBBER_HIDE_DELAY = 1000;
  const [showThrobber, setShowThrobber] = useState(isSaving);
  if (isSaving && !showThrobber) setShowThrobber(true);

  useEffect(() => {
    if (isSaving) return undefined;
    const id = setTimeout(() => {
      setShowThrobber(false);
    }, THROBBER_HIDE_DELAY);
    return () => {
      clearTimeout(id);
    };
  }, [isSaving]);

  const action = practitioner.data?.videoUrl ? 'Edit' : 'Add';

  const backUrl = practitionerId
    ? `${P_ADMIN_PRACTITIONERS}/${practitionerId}`
    : P_ADMIN_PROFILE;

  const save = async () => {
    try {
      setBusy(0);

      if (!(localVideo instanceof File)) throw Error('Not implemented yet');

      await uploadIntroVideo({
        file: localVideo,
        onProgress: (value) => {
          setBusy(Math.round(100 * value));
        },
        practitionerId: getId(practitioner.data),
      });

      setLocalVideo(undefined);
      await practitioner.reload();
    } catch (error) {
      captureException(error);
      // eslint-disable-next-line no-alert
      alert('Failed to save the video');
    } finally {
      setBusy(false);
    }
  };

  const removeExistingVideo = () => {
    void (async () => {
      try {
        setBusy(true);
        await deleteIntroVideo(getId(practitioner.data));
        await practitioner.reload();
      } catch (error) {
        captureException(error);
        // eslint-disable-next-line no-alert
        alert('Failed to remove the video');
      } finally {
        setBusy(false);
      }
    })();
  };

  const content = (
    <div styleName="wrapper">
      {showThrobber ? (
        <div styleName="savingOverlay">
          <Throbber progress={typeof busy === 'number' ? busy : undefined} />
        </div>
      ) : null}

      <div styleName="container">
        <div styleName="header">
          <div styleName="tabHandle">
            <IconVideo /> Upload Video
          </div>
        </div>
        <div styleName="mainContent">
          <AdminVideoUpload
            localVideo={localVideo}
            onRemoveExisting={removeExistingVideo}
            setLocalVideo={setLocalVideo}
            video={
              practitioner.data?.videoUrl
                ? {
                  isReadyToBePlayed:
                      practitioner.data.videoIsReadyToBePlayed ?? false,
                  url: practitioner.data.videoUrl,
                }
                : null
            }
          />
        </div>
        <div styleName="footer">
          <Button theme={noBorderButtonTheme} to={backUrl}>
            Cancel
          </Button>
          <Button
            disabled={!localVideo || isSaving}
            onClick={() => {
              void save();
            }}
          >
            Save Video
          </Button>
        </div>
      </div>
    </div>
  );

  if (practitionerId) {
    return content;
  }

  return (
    <AdminPage
      subTitle={`Manage your account / ${action} your intro video`}
      title={`${action} New Video`}
    >
      {content}
    </AdminPage>
  );
};

export default AdminPractitionerIntroVideo;
